import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, Check, AlertCircle, Sparkles, X } from 'lucide-react';

interface TeacherPasswordSetupProps {
  teacherName: string;
  onComplete: (password: string) => void;
}

function getPasswordStrength(password: string) {
  const hasMinLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumberOrSpecial = /[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);

  const checks = { hasMinLength, hasUppercase, hasNumberOrSpecial };
  const passed = [hasMinLength, hasUppercase, hasNumberOrSpecial].filter(Boolean).length;

  let level: 'weak' | 'medium' | 'strong' = 'weak';
  if (passed === 3) level = 'strong';
  else if (passed >= 2 && hasMinLength) level = 'medium';

  return { level, checks, passed, isValid: hasMinLength && hasUppercase && hasNumberOrSpecial };
}

export function TeacherPasswordSetup({ teacherName, onComplete }: TeacherPasswordSetupProps) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'intro' | 'form'>('intro');

  const strength = useMemo(() => getPasswordStrength(password), [password]);

  // Intro text animation
  const introLines = [
    "One more thing...",
    "Let's secure your account"
  ];

  const handleSubmit = () => {
    if (!strength.isValid) {
      setError('Password must be at least 8 characters with one uppercase letter and one number or special character');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    onComplete(password);
  };

  // Show intro animation first
  if (step === 'intro') {
    return (
      <div className="min-h-screen bg-hero-gradient flex flex-col items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center space-y-6"
        >
          {introLines.map((line, idx) => (
            <motion.p
              key={idx}
              initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{
                delay: idx * 1.2 + 0.5,
                duration: 0.8,
                ease: 'easeOut'
              }}
              className={`text-white font-medium ${
                idx === 0 ? 'text-xl opacity-80' : 'text-3xl md:text-4xl font-bold'
              }`}
            >
              {line}
            </motion.p>
          ))}

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3.5 }}
            onAnimationComplete={() => setTimeout(() => setStep('form'), 500)}
          >
            <Lock className="w-12 h-12 text-accent mx-auto animate-pulse" />
          </motion.div>
        </motion.div>
      </div>
    );
  }

  const strengthColor = strength.level === 'strong' ? 'bg-success' : strength.level === 'medium' ? 'bg-warning' : 'bg-destructive';
  const strengthLabel = strength.level === 'strong' ? 'Strong' : strength.level === 'medium' ? 'Medium' : 'Weak';
  const strengthTextColor = strength.level === 'strong' ? 'text-success' : strength.level === 'medium' ? 'text-warning-text' : 'text-destructive';

  return (
    <div className="min-h-screen bg-hero-gradient flex items-center justify-center p-4 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="w-full max-w-md"
      >
        <div className="glass-card p-8 md:p-10">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="w-16 h-16 rounded-2xl bg-accent/20 flex items-center justify-center mx-auto mb-4"
            >
              <Lock className="w-8 h-8 text-accent" />
            </motion.div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              Create Your Password
            </h1>
            <p className="text-muted-foreground">
              Hi {teacherName.split(' ')[0]}, set a password for future logins
            </p>
          </div>

          <div className="space-y-4">
            {/* Password input */}
            <div>
              <label htmlFor="teacher-setup-password" className="block text-sm font-semibold text-foreground mb-1">
                New Password
              </label>
              <div className="relative">
                <input
                  id="teacher-setup-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  className="input-premium pr-10"
                  placeholder="Enter password"
                  minLength={8}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Password strength meter */}
            {password && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                {/* Progress bar */}
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Password strength</span>
                    <span className={`text-xs font-semibold ${strengthTextColor}`}>{strengthLabel}</span>
                  </div>
                  <div className="flex gap-1">
                    <div className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                      strength.passed >= 1 ? strengthColor : 'bg-muted'
                    }`} />
                    <div className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                      strength.passed >= 2 && strength.checks.hasMinLength ? strengthColor : 'bg-muted'
                    }`} />
                    <div className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                      strength.level === 'strong' ? strengthColor : 'bg-muted'
                    }`} />
                  </div>
                </div>

                {/* Requirements checklist */}
                <div className="space-y-1">
                  {[
                    { met: strength.checks.hasMinLength, label: 'At least 8 characters' },
                    { met: strength.checks.hasUppercase, label: 'One uppercase letter' },
                    { met: strength.checks.hasNumberOrSpecial, label: 'One number or special character' },
                  ].map((req) => (
                    <div key={req.label} className="flex items-center gap-2 text-xs">
                      {req.met ? (
                        <Check size={14} className="text-success flex-shrink-0" />
                      ) : (
                        <X size={14} className="text-muted-foreground flex-shrink-0" />
                      )}
                      <span className={req.met ? 'text-success' : 'text-muted-foreground'}>
                        {req.label}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Confirm password */}
            <div>
              <label htmlFor="teacher-setup-confirm" className="block text-sm font-semibold text-foreground mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="teacher-setup-confirm"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  className="input-premium pr-10"
                  placeholder="Confirm password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Password match indicator */}
            {confirmPassword && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex items-center gap-2 text-sm ${
                  password === confirmPassword ? 'text-success' : 'text-muted-foreground'
                }`}
              >
                {password === confirmPassword ? (
                  <>
                    <Check size={16} />
                    <span>Passwords match</span>
                  </>
                ) : (
                  <>
                    <AlertCircle size={16} className="text-destructive" />
                    <span className="text-destructive">Passwords don't match</span>
                  </>
                )}
              </motion.div>
            )}

            {/* Error message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
              >
                <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0" />
                <span className="text-sm text-destructive font-medium">{error}</span>
              </motion.div>
            )}

            {/* Submit button */}
            <button
              onClick={handleSubmit}
              disabled={!password || !confirmPassword || !strength.isValid}
              className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Sparkles size={18} />
              Set Password & Continue
            </button>

            <p className="text-xs text-center text-muted-foreground mt-4">
              You'll use this password next time you log in
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
